import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
    Box, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material';

import { alpha } from '@mui/material/styles';

import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import UndoIcon from '@mui/icons-material/Undo';

import { RADIUS } from '../../theme/muiTheme';
import { formatDateTimeJst } from '../utils/format';
import { formatSize } from '../utils/number';

export const fileMatchesAccept = (file, accept) => {
    const acceptedTypes = String(accept || '').split(',')
        .map(value => value.trim().toLowerCase()).filter(Boolean);
    if (acceptedTypes.length === 0) return true;
    const fileName = String(file?.name || '').toLowerCase();
    const mimeType = String(file?.type || '').toLowerCase();
    return acceptedTypes.some((acceptedType) => {
        if (acceptedType.startsWith('.')) return fileName.endsWith(acceptedType);
        if (acceptedType.endsWith('/*')) return mimeType.startsWith(acceptedType.slice(0, -1));
        return mimeType === acceptedType;
    });
};

export default function FileDropField({
    file,
    currentName,
    currentMeta,
    onChange,
    accept = '.xlsx,.xls,.csv',
    maxSizeMb = 25,
    multiple = false,
    maxFiles = null,
    acceptLabel,
    storedLabel,
    pendingUploadLabel,
    onStoredFileClick,
    storedFileActionLabel,
    storedFileDisabled = false,
    externalError = '',
    fillHeight = false,
}) {
    const { t } = useTranslation();

    const [dragging, setDragging] = useState(false);
    const [error, setError] = useState('');

    const hasFile = Boolean(file || currentName);
    const displayName = file ? file.name : currentName;
    const displayMeta = file
        ? formatSize(file.size)
        : (formatDateTimeJst(currentMeta) || currentMeta);

    const accept_ = (picked) => {
        const pickedFiles = Array.from(picked || []).filter(Boolean);

        if (pickedFiles.length === 0) return;

        if (Number.isInteger(maxFiles) && pickedFiles.length > maxFiles) {
            setError(t('common.file.tooMany', {
                count: maxFiles,
                defaultValue: `Select no more than ${maxFiles} files.`,
            }));
            return;
        }

        if (pickedFiles.some(pickedFile => !fileMatchesAccept(pickedFile, accept))) {
            setError(t('common.file.invalidType', {
                accept,
                defaultValue: `Choose a supported file type: ${accept}.`,
            }));
            return;
        }

        if (pickedFiles.some(pickedFile => pickedFile.size > maxSizeMb * 1024 * 1024)) {
            setError(t('common.file.tooLarge', { max: maxSizeMb }));
            return;
        }

        setError('');
        onChange(multiple ? pickedFiles : pickedFiles[0]);
    };

    const pick = (event) => {
        accept_(event.target.files);
        event.target.value = '';   // allow re-picking the same file
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setDragging(false);
        accept_(event.dataTransfer.files);
    };

    const dragProps = {
        onDragOver: (event) => { event.preventDefault(); setDragging(true); },
        onDragEnter: (event) => { event.preventDefault(); setDragging(true); },
        onDragLeave: () => setDragging(false),
        onDrop: handleDrop,
    };

    return (
        <Stack
            spacing={1.25}
            {...dragProps}
            sx={fillHeight ? { height: '100%' } : undefined}
        >
            {hasFile && (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 1.25,
                        borderRadius: `${RADIUS.control}px`,
                        borderColor: 'divider',
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                            sx={(theme) => ({
                                width: 36,
                                height: 36,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: `${RADIUS.filter}px`,
                                bgcolor: alpha(
                                    file
                                        ? theme.palette.primary.main
                                        : theme.palette.success.main,
                                    0.12,
                                ),
                                color: file ? 'primary.main' : 'success.main',
                            })}
                        >
                            <DescriptionOutlinedIcon fontSize="small" />
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            {!file && onStoredFileClick ? (
                                <Typography
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    title={storedFileActionLabel || displayName}
                                    aria-label={storedFileActionLabel || displayName}
                                    disabled={storedFileDisabled}
                                    onClick={onStoredFileClick}
                                    sx={{
                                        appearance: 'none',
                                        bgcolor: 'transparent',
                                        border: 0,
                                        color: 'inherit',
                                        cursor: storedFileDisabled ? 'default' : 'pointer',
                                        display: 'block',
                                        maxWidth: '100%',
                                        p: 0,
                                        textAlign: 'left',
                                        textDecoration: 'underline',
                                        textDecorationThickness: 'from-font',
                                        textUnderlineOffset: '2px',
                                        '&:focus-visible': {
                                            borderRadius: 0.5,
                                            outline: '2px solid',
                                            outlineColor: 'primary.main',
                                            outlineOffset: 2,
                                        },
                                    }}
                                >
                                    {displayName}
                                </Typography>
                            ) : (
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    title={displayName}
                                >
                                    {displayName}
                                </Typography>
                            )}

                            <Typography variant="caption" color="text.secondary">
                                {file
                                    ? (pendingUploadLabel || t('common.file.pendingUpload'))
                                    : (storedLabel || t('common.file.stored'))}
                                {displayMeta ? ` · ${displayMeta}` : ''}
                            </Typography>
                        </Box>

                        {file && (
                            <Tooltip title={t('common.file.revert')}>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setError('');
                                        onChange(null);
                                    }}
                                    aria-label={t('common.file.revert')}
                                >
                                    <UndoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Paper>
            )}

            <Box
                component="label"
                sx={(theme) => ({
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.25,
                    py: hasFile ? 1.75 : 3,
                    px: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    border: '1px dashed',
                    borderColor: dragging ? 'primary.main' : 'divider',
                    borderRadius: `${RADIUS.control}px`,
                    bgcolor: dragging
                        ? alpha(theme.palette.primary.main, 0.06)
                        : theme.palette.background.default,
                    transition: theme.transitions.create(['border-color', 'background-color']),
                    '&:hover': { borderColor: 'primary.main' },
                    ...(fillHeight && { flex: 1, minHeight: 0 }),
                })}
            >
                <CloudUploadOutlinedIcon
                    fontSize="small"
                    sx={{ color: dragging ? 'primary.main' : 'text.disabled' }}
                />

                <Typography variant="body2" fontWeight={600}>
                    {hasFile
                        ? t('common.file.dropReplaceHint')
                        : t('common.file.dropHint')}{' '}
                    <Box
                        component="span"
                        sx={{ color: 'primary.main' }}
                    >
                        {t('common.file.browse')}
                    </Box>
                </Typography>

                <Typography variant="caption" color="text.secondary">
                    {acceptLabel || t('common.file.accepted', { max: maxSizeMb })}
                </Typography>

                <input
                    hidden
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    onChange={pick}
                />
            </Box>

            {(error || externalError) && (
                <Typography
                    variant="caption"
                    color="error.main"
                    role="alert"
                >
                    {error || externalError}
                </Typography>
            )}
        </Stack>
    );
}